import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useInView } from "react-intersection-observer";

import { BIT_PROP_TYPES } from "@constants/best-in-travel/2025/proptypes";

import { keyify } from "@utils/keyify";
import { useBestInTravel } from "@context/bestInTravel/2025/bestInTravelContext";
import { tagular } from "@cohesion/tagular";

import { NewsletterWidget } from "@components/newsletter/newsletterWidget";
import { Shop } from "@pageComponents/best-in-travel-2025/shop";
import { DestinationSection } from "@pageComponents/best-in-travel-2025/destinationSection";
import { SailyDestinationSection } from "@pageComponents/best-in-travel-2025/saily/sailyDestinationSection";

export function DestinationsWrapper({ destinations }) {
  const { isClient, sectionRefs, screen } = useBestInTravel();
  const destinationsContainerRef = useRef(null);
  const partnershipPosition = 6; // Position to insert Saily partnership section

  const { ref: booksRef, inView: booksInView } = useInView({
    threshold: 1,
    triggerOnce: true,
  });

  useEffect(() => {
    if (booksInView) {
      tagular("view", {
        webElement: {
          location: "BIT_Book_Section",
          elementType: "SECTION",
        },
      });
    }
  }, [booksInView, booksRef]);

  // Set media orientation based on screen size:
  // Use 'portrait' for small screens (like mobile devices) and 'landscape' for larger screens.
  const orientation = ["sm", "md"].includes(screen) ? "portrait" : "landscape";

  return (
    <div
      id="destinations-container"
      ref={destinationsContainerRef}
      className="overflow-y-scroll snap-y snap-mandatory h-screen-dvh no-scrollbar snap-always"
    >
      {destinations.map((destination, index) => {
        // Filter media data to include only items with the correct orientation:
        // - Videos must match the specified orientation.
        // - Images without a specified orientation default to 'landscape', or must match if specified.
        const selectedMedia = destination.media.filter(
          media =>
            (media.type === "video" && media.orientation === orientation) ||
            (media.type === "image" &&
              ((!media.orientation && orientation === "landscape") ||
                media.orientation === orientation))
        );

        // Create a modified destination object that includes only the selected media
        const modifiedDestination = {
          ...destination,
          media: selectedMedia,
        };

        // Adjust the index for subsequent sections after the partnership section is inserted
        const modifiedIndex = index >= partnershipPosition ? index + 1 : index;

        return (
          <div key={destination.name}>
            {/* PARTNERSHIP SECTION */}
            {index === partnershipPosition && (
              <section
                ref={el => {
                  sectionRefs.current[partnershipPosition] = el;
                }}
                key="partnership-section"
                id="partnership-section"
                className="relative w-full h-screen-dvh bg-black-100 snap-start snap-always"
              >
                {isClient ? (
                  <SailyDestinationSection index={partnershipPosition} />
                ) : (
                  <div>Loading...</div>
                )}
              </section>
            )}

            {/* DESTINATION SECTION */}
            <section
              ref={el => {
                sectionRefs.current[modifiedIndex] = el;
              }}
              key={destination.name}
              id={keyify(destination.name)}
              className="relative w-full h-screen-dvh bg-black-100 snap-start snap-always"
            >
              <DestinationSection
                destination={modifiedDestination}
                index={modifiedIndex}
                totalDestinations={destinations.length + 1}
              />
            </section>
          </div>
        );
      })}

      <div
        className="h-screen-dvh snap-always snap-start sm:flex"
        key="footer-section"
      >
        <section
          ref={booksRef}
          className="h-screen-dvh snap-start sm:w-1/2 sm:snap-none snap-always"
        >
          <Shop backgroundImage="https://lonelyplanetstatic.imgix.net/marketing/2024/newsletter/topography-background-sm.svg" />
        </section>
        <section className="h-screen-dvh snap-start sm:w-1/2 sm:snap-none snap-always">
          <NewsletterWidget
            topographyBackground
            imageClasses="object-right"
            stackedLayout
            stackedForm
            formId="Best_in_travel"
            classNames="flex items-center flex-col justify-center h-full pt-20"
            wrapperClassNames="h-full"
            titleClassNames="font-instrument text-4xl font-normal md:text-5xl lg:text-5xl md:w-96 mt-8 mb-6"
            formClassNames="sm:px-2 md:px-0 md:w-96 md:mx-auto"
            backgroundImage="https://lonelyplanetstatic.imgix.net/marketing/best-in-travel/2025/topography-background-newsletter.svg"
            backgroundImageMobile="https://lonelyplanetstatic.imgix.net/marketing/best-in-travel/2025/topography-background-newsletter-sm.svg"
          />
        </section>
      </div>
    </div>
  );
}

DestinationsWrapper.propTypes = {
  destinations: PropTypes.arrayOf(BIT_PROP_TYPES.destination).isRequired,
};
